/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

/**
 * The `CloseProps` interface defines the properties that can be passed to the `CloseIcon` component.
 * These properties allow customization of the component's CSS class, color, dimensions, and testing identifiers.
 */
interface CloseProps {
    /**
     * A string representing the CSS class to be applied to the CloseIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#0050FF'
     */
    color1?: string;
    /**
     * A number or string representing the height of the CloseIcon element.
     *
     * @default 24
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Close'
     */
    testId?: string;
    /**
     * A number or string representing the height of the CloseIcon element.
     *
     * @default 24
     */
    width?: number | string;
}

/**
 * The `Close` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `CloseProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the CloseIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the CloseIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the CloseIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const CloseComponent = <Close className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Close = ({
    className = '',
    color1 = '#0050FF',
    height = 24,
    ref,
    testId = 'Close',
    width = 24
}: CloseProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 24 24"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path clipRule="evenodd" d="M18.53 6.53a.75.75 0 0 0-1.06-1.06L12 10.94 6.53 5.47a.75.75 0 0 0-1.06 1.06L10.94 12l-5.47 5.47a.75.75 0 1 0 1.06 1.06L12 13.06l5.47 5.47a.75.75 0 1 0 1.06-1.06L13.06 12l5.47-5.47Z" fill={color1} fillRule="evenodd" />
    </svg>
);

Close.displayName = 'Close';